import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PaginationInfo } from '~/api/fragments.generated';
import { PER_PAGE } from '~/lib/pagination';

export const usePaginationParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') || 1);
  const perPage = Number(searchParams.get('perPage') || PER_PAGE);

  const setPageParams = useCallback(
    ({ page, perPage }: { page?: number; perPage?: number }) =>
      setSearchParams((x) => ({
        ...Object.fromEntries(x),
        ...(page && { page: String(page) }),
        ...(perPage && { perPage: String(perPage) }),
      })),
    [setSearchParams],
  );

  const handleOutOfBounds = useCallback(
    (pageInfo: PaginationInfo | undefined) => {
      if (pageInfo?.currentPage === page && pageInfo.perPage === perPage) return;
      setPageParams({ page: pageInfo?.currentPage, perPage: pageInfo?.perPage });
    },
    [page, perPage, setPageParams],
  );

  const paginationModel = useMemo(
    () => ({
      page: page - 1,
      pageSize: perPage,
    }),
    [page, perPage],
  );

  return { handleOutOfBounds, page, paginationModel, perPage, setPageParams };
};

export const useTotalCount = (totalCount: number | undefined) => {
  const totalCountRef = useRef(totalCount ?? 0);
  return useMemo(() => {
    if (totalCount !== undefined) totalCountRef.current = totalCount;
    return totalCountRef.current;
  }, [totalCount]);
};
